/**
 * Stage 1 ad-click capture.
 *
 * Records the Google Ads click (gclid / gbraid / wbraid) that brought the
 * visitor in, keeps it for 90 days across pages, and stamps it onto every
 * lead form at submit time as hidden fields. The click is stored per lead
 * submission server-side — never derived later.
 *
 * Consent: capture is skipped in regions that require prior consent
 * (EEA/UK/CH; Quebec is handled by Google's regional defaults). The consent
 * state at submit time is sent with the lead as consent_ad_user_data.
 */

import { canTrackAdsSync, isConsentRequiredRegion } from "@/lib/adsConsent";
import { captureChecklistVisit } from "@/lib/checklistVisit";

export interface AdClick {
  click_id: string;
  click_id_type: "gclid" | "gbraid" | "wbraid";
  click_at: string; // ISO timestamp of when the click was observed
}

const STORAGE_KEY = "trn_ad_click";
const TTL_MS = 90 * 24 * 60 * 60 * 1000;
const CLICK_PARAMS: AdClick["click_id_type"][] = ["gclid", "gbraid", "wbraid"];

function readStored(): AdClick | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AdClick & { t?: number };
    if (!parsed.click_id || !parsed.click_id_type) return null;
    if (typeof parsed.t === "number" && Date.now() - parsed.t > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return { click_id: parsed.click_id, click_id_type: parsed.click_id_type, click_at: parsed.click_at };
  } catch {
    return null;
  }
}

/**
 * Call on every route change. If the current URL carries an ad click ID,
 * it replaces the stored one (a newer ad click wins); otherwise the stored
 * click is left untouched.
 */
export function captureAdClick(): void {
  if (typeof window === "undefined") return;
  captureChecklistVisit();
  // Pre-warm the region lookup so it has settled before any form submit.
  void isConsentRequiredRegion();
  const params = new URLSearchParams(window.location.search);
  for (const type of CLICK_PARAMS) {
    const value = params.get(type);
    if (value) {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ click_id: value, click_id_type: type, click_at: new Date().toISOString(), t: Date.now() }),
        );
      } catch {
        /* storage unavailable */
      }
      return;
    }
  }
}

function setHidden(form: HTMLFormElement, name: string, value: string): void {
  let input = form.querySelector<HTMLInputElement>(`input[name='${name}']`);
  if (!input) {
    input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    form.appendChild(input);
  }
  input.value = value;
}

/**
 * Stamps the stored ad click (and consent state) onto the form as hidden
 * fields. Safe to call from a capture-phase submit listener: it runs before
 * React handlers and before the native POST reads the form data.
 */
export function stampAdClickOnForm(form: HTMLFormElement): void {
  try {
    const click = readStored();
    // Must be synchronous: the native POST serializes the form right after
    // this capture-phase listener returns.
    const allowed = canTrackAdsSync();
    setHidden(form, "consent_ad_user_data", allowed ? "granted" : "denied");
    if (!click || !allowed) return;
    setHidden(form, "click_id", click.click_id);
    setHidden(form, "click_id_type", click.click_id_type);
    setHidden(form, "click_at", click.click_at);
  } catch {
    /* never block a submission */
  }
}
